/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { RateLimitModal } from '../rate-limit';

export interface GlobalDialogsProps {
    /**
     * Set to false when this app mounts the rate-limit notice itself, or means to
     * show nothing at all for a 429. It only controls whether the shared stack
     * mounts the notice — detecting 429s is a separate, per-app decision either
     * way, and `useRateLimit` keeps working regardless. See `shared-ui/rate-limit`.
     */
    mountRateLimitNotice?: boolean;
}

/**
 * Host for the platform's global dialogs: app-wide overlays owned centrally rather
 * than by any one page, mounted once so every app words them identically without
 * each team deciding where to put a dialog.
 *
 * Rendered by `ExternalAppProviders`, because that is the one thing every external
 * app already mounts and so the one place a dialog cannot be forgotten.
 *
 * This host exists to be a named home with a stated boundary, rather than a caveat
 * on the provider stack's contract. A dialog belongs here only if all four hold:
 *
 * 1. **Portal-rendered.** It leaves this subtree for `document.body`, so the host
 *    adds no DOM of its own even while open and apps keep sole ownership of their
 *    layout and landmark structure.
 * 2. **No layout participation.** No place in the flex column, the heading
 *    hierarchy, or the `<main>` / `<nav>` landmark set.
 * 3. **Inert by default.** It renders nothing until something raises it, so an app
 *    that never triggers it pays nothing for having it mounted.
 * 4. **Platform-owned.** Its wording and behavior are owned centrally and being
 *    identical everywhere is the point. Anything a page configures or a feature
 *    owns is page content, and belongs in the page.
 *
 * An important caveat: these modals have access to all typical app providers
 * EXCEPT React Router, which is rendered by the app generally as the first child
 * of the ExternalAppProviders stack.
 */
export function GlobalDialogs({ mountRateLimitNotice = true }: GlobalDialogsProps) {
    return <>{mountRateLimitNotice && <RateLimitModal />}</>;
}
